import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, FileText, CheckCircle2, ShieldCheck, Layers, PlayCircle, Clock, Sparkles, ArrowRight, Zap } from 'lucide-react';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Counter } from './ui/Counter';
import { AgencyModal } from './AgencyModal';
import { InteractiveProfile } from './InteractiveProfile';
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.21, 0.47, 0.32, 0.98]
    }
  }
};
// Floating activity chip
const FloatChip = ({
  className = '',
  icon,
  label,
  delay = 0
}: {
  className?: string;
  icon: React.ReactNode;
  label: string;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  scale: 0.8,
  y: 10
}} animate={{
  opacity: 1,
  scale: 1,
  y: 0
}} transition={{
  delay,
  duration: 0.5,
  ease: 'backOut'
}} className={`absolute z-20 hidden lg:flex items-center gap-2 bg-white/90 backdrop-blur-md border border-gray-100 shadow-float rounded-full pl-2 pr-3.5 py-1.5 ${className}`} data-id="element-387">
    <motion.div animate={{
    y: [0, -5, 0]
  }} transition={{
    duration: 3.5,
    repeat: Infinity,
    ease: 'easeInOut',
    delay
  }} className="flex items-center gap-2" data-id="element-388">
      {icon}
      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap" data-id="element-389">
        {label}
      </span>
    </motion.div>
  </motion.div>;
export const Hero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-id="element-390">
      <AgencyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-391" />
      {/* Animated gradient mesh background */}
      <motion.div animate={{
      x: [0, 40, 0],
      y: [0, -30, 0]
    }} transition={{
      duration: 18,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[1100px] h-[560px] bg-zano-lavender/50 rounded-full blur-3xl -z-10" data-id="element-392" />
      <motion.div animate={{
      x: [0, -40, 0],
      y: [0, 30, 0]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-32 right-[-5%] w-[560px] h-[560px] bg-zano-blush/60 rounded-full blur-3xl -z-10" data-id="element-393" />
      <motion.div animate={{
      x: [0, 30, 0]
    }} transition={{
      duration: 22,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-52 left-[-5%] w-[440px] h-[440px] bg-sky-100/50 rounded-full blur-3xl -z-10" data-id="element-394" />
      {/* Subtle grid */}
      <div className="absolute inset-0 -z-10 opacity-[0.4]" style={{
      backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.025) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
      maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 40%, transparent 75%)'
    }} data-id="element-395" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-396">
        <motion.div variants={container} initial="hidden" animate="show" className="max-w-4xl mx-auto" data-id="element-397">
          <motion.div variants={item} data-id="element-398">
            <Badge className="mb-6 bg-white/80 backdrop-blur text-zano-pink border border-zano-pink/30 shadow-sm" data-id="element-399">
              <Sparkles size={13} className="mr-1.5" data-id="element-400" /> The Elite Creator
              Network
            </Badge>
          </motion.div>

          <motion.h1 variants={item} className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-zano-charcoal mb-6 leading-[1.08]" data-id="element-401">
            The discovery layer for{' '}
            <span className="relative inline-block" data-id="element-402">
              <span className="relative z-10 text-zano-pink" data-id="element-403">
                represented influencers
              </span>
              <motion.span initial={{
              scaleX: 0
            }} animate={{
              scaleX: 1
            }} transition={{
              delay: 0.7,
              duration: 0.6,
              ease: 'easeOut'
            }} className="absolute bottom-1 left-0 right-0 h-3 bg-zano-pink/15 -z-0 origin-left rounded" data-id="element-404" />
            </span>
          </motion.h1>

          <motion.p variants={item} className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-405">
            Search live agency rosters and connect directly with the managers
            who represent the world's best creators.
          </motion.p>

          <motion.div variants={item} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-406">
            <a href="https://www.youtube.com/watch?v=U2sV81k41u4&pp=0gcJCUwLAYcqIYzv" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-407">
              <Button variant="secondary" className="w-full sm:w-auto text-base px-8 py-3.5" data-id="element-408">
                <PlayCircle size={18} className="mr-1.5" data-id="element-409" /> Watch Demo Video
              </Button>
            </a>
            <Link to="/pricing" className="w-full sm:w-auto" data-id="element-410">
              <Button variant="primary" className="w-full sm:w-auto text-base px-8 py-3.5" data-id="element-411">
                Start Free Trial <ArrowRight size={18} className="ml-1.5" data-id="element-412" />
              </Button>
            </Link>
          </motion.div>

          {/* Trust strip */}
          <motion.div variants={item} className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-3" data-id="element-413">
            <div className="flex items-center -space-x-2" data-id="element-414">
              <div className="w-8 h-8 rounded-full border-2 border-white bg-white overflow-hidden shadow-sm relative z-0" data-id="element-415">
                <img src="https://cdn.magicpatterns.com/uploads/aQUTmU5LHPMMPv6uVqWKZY/Screenshot_2026-07-06_at_16.16.09.png" alt="Viral Nation logo" className="w-full h-full object-cover scale-[2.1]" data-id="element-416" />
              </div>
              <div className="w-8 h-8 rounded-full border-2 border-white bg-white overflow-hidden shadow-sm relative z-10 -ml-4" data-id="element-417">
                <img src="https://cdn.magicpatterns.com/uploads/iMEkKfrRdejvDfRHdG5tcE/uta.png" alt="UTA logo" className="w-full h-full object-contain" data-id="element-418" />
              </div>
              {[{
              name: 'Connect Management',
              src: 'https://cdn.magicpatterns.com/uploads/fKq6FRuDXeQ3mJ1bWbqkVi/connectmgmt.png'
            }, {
              name: 'Clicks Talent',
              src: 'https://cdn.magicpatterns.com/uploads/8tVZ1Wn6UGbeE2qh1vffiq/clicks.jpg'
            }, {
              name: 'Famfluence',
              src: 'https://cdn.magicpatterns.com/uploads/iq5ZMVZ2aiG8SYPyU7XxgR/famfluence_logo.png'
            }].map((p, i) => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-white overflow-hidden shadow-sm relative z-0" data-id="element-419">
                  <img src={p.src} alt={`${p.name} logo`} className="w-full h-full object-contain" data-id="element-420" />
                </div>)}
            </div>
            <p className="text-sm text-gray-500 font-medium" data-id="element-421">
              <span className="font-semibold text-zano-charcoal" data-id="element-422">
                <Counter to={400} suffix="+" data-id="element-423" />
              </span>{' '}
              talent agencies ·{' '}
              <span className="font-semibold text-zano-charcoal" data-id="element-424">
                <Counter to={6000} suffix="+" data-id="element-425" />
              </span>{' '}
              represented creators
            </p>
          </motion.div>
        </motion.div>

        {/* Interactive Profile Explainer */}
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.35
      }} className="mt-24 relative mx-auto max-w-6xl text-left" data-id="element-426">
          <InteractiveProfile data-id="element-427" />
        </motion.div>
      </div>
    </section>;
};